import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  Box,
  Text,
  Field,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  Textarea,
  Button,
  VStack,
  Select,
  Checkbox,
  Link,
  createListCollection,

} from '@chakra-ui/react';
import { instrumentTexts } from "../i18n/instruments";
import { contractTexts } from "../i18n/contracts";
import { LanguageContext } from "../contexts/LanguageContext";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const labelStyles = {
  fontFamily: "space",
  fontSize: "xs",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "gray.600",
};

const inputStyles = {
  bg: "white",
  borderColor: "gray.300",
  color: "gray.900",
  _placeholder: { color: "gray.400" },
  _focusVisible: { borderColor: "gray.900", boxShadow: "none" },
};

const SummaryRow = ({ label, value, strong }) => (
  <Flex justify="space-between" gap={4} py={1.5} borderBottomWidth="1px" borderColor="gray.100" _last={{ borderBottomWidth: 0 }}>
    <Text fontSize="sm" color="gray.500">{label}</Text>
    <Text fontSize="sm" color={strong ? "gray.900" : "gray.800"} fontWeight={strong ? "semibold" : "medium"} textAlign="right">
      {value}
    </Text>
  </Flex>
);

const RentalForm = ({ userId }) => {
  const { language } = useContext(LanguageContext);
  const t = instrumentTexts[language] || instrumentTexts["et"];
  const ct = contractTexts[language] || contractTexts["et"];
  const location = useLocation();
  const instrument = location.state?.instrument;
  const [rentalPeriod, setRentalPeriod] = useState(12);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [rate, setRate] = useState(null);
  const [finalRate, setFinalRate] = useState(null);
  const [invoiceInterval, setInvoiceInterval] = useState(1);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();


  /* const getTokenFromCookie = () => {
    const name = 'access_token=';
    const decodedCookie = decodeURIComponent(document.cookie); // Decode the cookie
    const cookieArr = decodedCookie.split(';'); // Split by ';' to get individual cookies
    
    // Loop through cookies to find the access_token
    for (let i = 0; i < cookieArr.length; i++) {
        let cookie = cookieArr[i].trim(); // Trim whitespace
        // Check if this cookie starts with access_token=
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length, cookie.length); // Return the token value
        }
    }
    return null; // Return null if token not found
  }; */

  const getCSRFToken = () => {
    const name = 'csrftoken'; // Adjust if your CSRF cookie has a different name
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

useEffect(() => {
  // Set a timeout to navigate back to the homepage after 180 seconds (3 minutes)
  const timer = setTimeout(() => {
    navigate('/');
  }, 120000); // 180000 milliseconds = 3 minutes

  // Clean up the timer if the component unmounts before the timer finishes
  return () => clearTimeout(timer);
}, [navigate]);
  

  // Fetch rate based on price_level when the component mounts
  useEffect(() => {
    if (instrument?.price_level) {
      fetch(`${API_BASE_URL}/api/rates/${instrument.price_level}`)
        .then(response => response.json())
        .then(data => {
          setRate(data.rate);
          setFinalRate(data.rate);
        })
        .catch(error => {
          console.error('Error fetching rate:', error);
          toast.error('Unable to retrieve rental rate information.', {
            duration: 4000,
            position: 'top-right',
          });
        });
    }
  }, [instrument]);

  // Update final rate based on rental period
  // Update final rate based on rental period
  useEffect(() => {
    if (!rate || !rentalPeriod) return;

    let updatedRate;
    if (rentalPeriod < 4) {
      updatedRate = Math.round(rate * 2.1);
    } else if (rentalPeriod < 12) {
      updatedRate = Math.round(rate * 1.4);
    } else {
      updatedRate = rate;
    }
    setFinalRate(updatedRate);

    const intervals = getInvoiceIntervals(rentalPeriod);

    // only reset if current selection is no longer valid
    if (!intervals.includes(invoiceInterval)) {
      setInvoiceInterval(intervals[0]);
    }
    console.log("interval:", invoiceInterval, typeof invoiceInterval);
  }, [rentalPeriod, rate, invoiceInterval]);


  const getInvoiceIntervals = (period) => {
    const p = Number(period);
    if (!p || p < 1) return [];
    const intervals = [];
    for (let i = 1; i <= p; i++) {
      if (p % i === 0) intervals.push(i);
    }
    return intervals;
  };

   const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
        toast.error(ct.confirm, {
            duration: 4000,
            position: 'top-right',
        });
        return;
    }

    const agreementData = {
        instrumentId: instrument.instrumentId,
        months: rentalPeriod,
        rate: finalRate,
        info: additionalInfo,
        invoiceInterval: invoiceInterval,
    };

    try {
      //const accessToken = getTokenFromCookie();
      const csrfToken = getCSRFToken();
      
      /* if (!accessToken) {
            throw new Error("No access token found");
        } */

        const response = await fetch(`${API_BASE_URL}/api/agreements/`, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                //"Authorization": `Bearer ${accessToken}`,
                "X-CSRFToken": csrfToken,
            },
            body: JSON.stringify(agreementData),
            credentials: "include",
        });

        if (response.ok) {
            toast.success('Your rental agreement has been successfully submitted!', {
                duration: 4000,
                position: 'top-right',
            });
            navigate('/contracts');
        } else if (response.status === 403) {
            // Profile incomplete (address / email / phone) — finish it first
            toast.error('Please complete your profile before renting.', {
                duration: 4000,
                position: 'top-right',
            });
            navigate('/profile');
        } else {
            console.error("Error details:", await response.text());
            toast.error('There was an issue submitting your agreement.', {
                duration: 4000,
                position: 'top-right',
            });
        }
    } catch (error) {
        console.error('Error creating rental agreement:', error);
        toast.error('Network error during agreement submission.', {
            duration: 4000,
            position: 'top-right',
        });
    }
  };

  const handleTermsClick = () => {
  const terms_en = [
    "Get familiar with explained terms on FAQ site. The main rental terms:",
    "The renter must follow the instrument usage instructions.",
    "Late returns may result in additional fees.",
    "Monthly rent must be paid on agreed dates.",
    "Late payments incur a 0.5% daily penalty.",
    "The instrument must be used carefully and for its intended purpose.",
    "The renter is responsible for damages, loss, or theft.",
    "The instrument must be returned in normal condition (fair wear excepted).",
    "Early termination requires payment of two additional months.",
    "The renter is responsible for returning the instrument."
  ];

  const terms_et = [
    "Tuvuge kindlasti pkemalt selgitatud tingimustega infolehel KKK. Peamised renditingimused:",
    "Rentnik peab järgima eseme kasutusjuhiseid.",
    "Hilinenud tagastamine võib kaasa tuua lisatasusid.",
    "Renditasu tuleb tasuda kokkulepitud kuupäevadel.",
    "Hilinenud maksete eest rakendub 0,5% viivis päevas.",
    "Ese peab olema kasutatud hoolikalt ja sihtotstarbeliselt.",
    "Rentnik vastutab kahju, kaotsimineku või varguse eest.",
    "Ese tuleb tagastada normaalses seisukorras (loomulik kulumine lubatud).",
    "Ennetähtaegsel lõpetamisel tuleb tasuda kahe kuu renditasu.",
    "Eseme tagastamise kohustus lasub rentnikul (sh vajadusel transportimine)."
  ];

  const terms = language === "et" ? terms_et : terms_en;

  const message = terms
    .map((line, index) => `${index + 1}. ${line}`)
    .join("\n");

  toast(message, {
    icon: "ℹ️",
    duration: 40000,
    position: "top-right",
    style: {
      whiteSpace: "pre-line",
      maxWidth: "420px",
    },
  });
};


  const invoiceIntervalCollection = createListCollection({
  items: getInvoiceIntervals(rentalPeriod).map((i) => ({
    label: `${i} ${i === 1 ? ct.months.slice(0, -1) : ct.months}`,
    value: String(i),
  })),
});

  return (
    <Box minH="calc(100vh - 4rem)" bg="gray.50" py={{ base: 8, md: 12 }} px={4}>
      <Toaster />
      <Box
        maxW="600px"
        mx="auto"
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <VStack gap={5} align="stretch">
          <Box textAlign="center">
            <Text
              fontFamily="space"
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="gray.500"
              mb={2}
            >
              {ct.heading}
            </Text>
            <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900" lineHeight="1.1">
              {instrument?.modelId.brand} {instrument?.modelId.model}
            </Heading>
          </Box>

          {instrument?.thumbnail_image && (
            <Flex justify="center">
              <Box bg="gray.100" borderRadius="lg" borderWidth="1px" borderColor="gray.200" overflow="hidden" h="120px" w="160px" display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={`${API_BASE_URL}${instrument.thumbnail_image}`}
                  alt={`${instrument.modelId.brand} ${instrument.modelId.model}`}
                  objectFit="contain"
                  h="100%"
                />
              </Box>
            </Flex>
          )}

          <Field.Root id="rentalPeriod" required>
            <Field.Label {...labelStyles}>{ct.period}</Field.Label>
            <Input
              {...inputStyles}
              type="number"
              value={rentalPeriod}
              onChange={(e) => setRentalPeriod(Number(e.target.value))}
              min="1"
              placeholder={ct.periodHelp}
            />
          </Field.Root>

          <Field.Root id="invoiceInterval" required>
            <Field.Label {...labelStyles}>{ct.interval}</Field.Label>

            <Select.Root
              collection={invoiceIntervalCollection}
              value={[String(invoiceInterval)]}
              onValueChange={(details) => {
                setInvoiceInterval(Number(details.value[0]));
              }}
            >
              <Select.Trigger {...inputStyles}>
                <Select.ValueText />
              </Select.Trigger>

              <Select.Content>
                {invoiceIntervalCollection.items.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    {item.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Field.Root>

          <Field.Root id="additionalInfo">
            <Field.Label {...labelStyles}>{ct.info}</Field.Label>
            <Textarea
              {...inputStyles}
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              placeholder={ct.notes}
              resize="vertical"
            />
          </Field.Root>

          <Field.Root>
            <HStack align="center" gap={3}>
              <Checkbox.Root
                checked={termsAccepted}
                onCheckedChange={(e) => setTermsAccepted(e.checked)}
                colorPalette="gray"
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label color="gray.700" fontSize="sm">{ct.termsok}</Checkbox.Label>
              </Checkbox.Root>
              <Link
                color="gray.900"
                onClick={handleTermsClick}
                fontSize="sm"
                fontWeight="medium"
                textDecoration="underline"
                textUnderlineOffset="3px"
                _hover={{ color: "gray.600" }}
              >
                {ct.view}
              </Link>
            </HStack>
          </Field.Root>

          <Box>
            <Text
              fontFamily="space"
              fontSize="xs"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color="gray.500"
              mb={2}
            >
              {ct.summary}
            </Text>
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" px={5} py={3}>
              <SummaryRow label={t.labels.brand} value={instrument?.modelId.brand} />
              <SummaryRow label={t.labels.model} value={instrument?.modelId.model} />
              <SummaryRow label={ct.period} value={rentalPeriod} />
              <SummaryRow
                label={ct.interval}
                value={`${invoiceInterval} ${invoiceInterval === 1 ? ct.months.slice(0, -1) : ct.months}`}
              />
              <SummaryRow
                label={ct.rate}
                value={finalRate !== null ? `${finalRate} €` : '…'}
                strong
              />
            </Box>
          </Box>

          <Button
            mt={2}
            size="lg"
            w="full"
            bg="#09090b"
            color="white"
            borderRadius="sm"
            fontFamily="space"
            fontSize="xs"
            fontWeight="500"
            letterSpacing="0.14em"
            textTransform="uppercase"
            _hover={{ bg: "gray.800" }}
            type="submit"
            onClick={handleSubmit}
          >
            {ct.submit}
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default RentalForm;
